import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { CookieConsent } from "@/components/common/CookieConsent";
import { MainLayout } from "@/components/layout/main-layout";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { MarketingAnalyticsProvider } from "@/contexts/MarketingAnalyticsContext";
import { OnboardingModalProvider } from "@/contexts/OnboardingModalContext";
import { ImpactAnalysisModalProvider } from "@/contexts/ImpactAnalysisModalContext";
import { RiskAnalysisModalProvider } from "@/contexts/RiskAnalysisModalContext";
import { SessionSwitcherProvider } from "@/contexts/SessionSwitcherContext";
import { envConfig } from "@/lib/config";
import { queryClient } from "@/lib/queryClient";
import AnalyzaEfectPerturbator from "./pages/AnalyzaEfectPerturbator";
import CompaniesPortfolio from "./pages/CompaniesPortfolio";
import Auth from "./pages/Auth";
import Logout from "./pages/Logout";
import AuthCallback from "./pages/AuthCallback";
import StatusEntitateDashboard from "./pages/StatusEntitateDashboard";
import { DecizieInregistrare } from "./pages/Onboarding/DecizieInregistrare";
import { AnalizaRisc } from "./pages/Onboarding/AnalizaRisc";
import { ImpactAnalysis } from "./pages/Onboarding/ImpactAnalysis";
import EvaluareMaturitate from "./pages/EvaluareMaturitate";
import GhidAnalizaRisc from "./pages/GhidAnalizaRisc";
import GhidMaturitate from "./pages/GhidMaturitate";
import GhidNis2 from "./pages/GhidNis2";
import GhidNotificare from "./pages/GhidNotificare";
import LandingAlternative from "./pages/LandingAlternative";
import LandingAlternativeV3 from "./pages/LandingAlternativeV3";
import LandingAlternativeV31 from "./pages/LandingAlternativeV31";
import LandingAlternativeV40 from "./pages/LandingAlternativeV40";
import CyFunFundamentals from "./pages/CyFunFundamentals";
import NIS2FAQ from "./pages/NIS2FAQ";
import NIS2FAQAnswer from "./pages/NIS2FAQAnswer";
import NIS2FAQBrowse from "./pages/NIS2FAQBrowse";
import NIS2Guide from "./pages/NIS2Guide";
import NotFound from "./pages/NotFound";
import NIS2Wizard from "./pages/nis2-wizard";
import PaymentProcessing from "./pages/payment-processing";
import PlanRemediere from "./pages/PlanRemediere";
import Profile from "./pages/Profile";
import Pricing from "./pages/Pricing";
import ReferralLanding from "./pages/ReferralLanding";
import ResetPassword from "./pages/ResetPassword";
import FileExtractionTest from "./pages/FileExtractionTest";
import DebugCompanyPreview from "./pages/DebugCompanyPreview";
import { FormularNotificare } from "./pages/Onboarding/FormularNotificare";
import RiskWizardPage from "./pages/AnalyzaRiscV2/RiskWizardPage";
import AnalyzaRiscResolver from "./pages/AnalyzaRiscV2/AnalyzaRiscResolver";
import Contact from "./pages/Contact";
import LegalDocuments from "./pages/LegalDocuments";
import TermeniSiConditii from "./pages/TermeniSiConditii";
import PoliticaDeConfidentialitate from "./pages/PoliticaDeConfidentialitate";
import PoliticaDeRambursare from "./pages/PoliticaDeRambursare";
import PoliticaDeCookieuri from "./pages/PoliticaDeCookieuri";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

const LEGAL_HASH_MAP: Record<string, string> = {
  "#terms": "/termeni-si-conditii",
  "#privacy": "/politica-de-confidentialitate",
  "#refund": "/politica-de-rambursare",
  "#cookies": "/politica-de-cookie-uri",
};

function LegalRedirect() {
  const hash = window.location.hash;
  const target = LEGAL_HASH_MAP[hash] ?? "/termeni-si-conditii";
  return <Navigate to={target} replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <MarketingAnalyticsProvider>
        <SessionSwitcherProvider>
          <TooltipProvider>
            <Toaster />
            <BrowserRouter>
              <OnboardingModalProvider>
                <RiskAnalysisModalProvider>
                  <ImpactAnalysisModalProvider>
                    <Routes>
                      <Route path="/" element={<LandingAlternativeV40 />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/logout" element={<Logout />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route
                        path="/reset-password"
                        element={
                          <ProtectedRoute>
                            <ResetPassword />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/ref/:code" element={<ReferralLanding />} />
                      <Route path="/despre-nis2" element={<NIS2Guide />} />

                      {/* Guide pages (public with sidebar) */}
                      <Route
                        path="/ghid-nis2"
                        element={
                          <MainLayout>
                            <GhidNis2 />
                          </MainLayout>
                        }
                      />
                      <Route
                        path="/ghid-notificare"
                        element={
                          <MainLayout>
                            <GhidNotificare />
                          </MainLayout>
                        }
                      />
                      <Route
                        path="/ghid-analiza-risc"
                        element={
                          <MainLayout>
                            <GhidAnalizaRisc />
                          </MainLayout>
                        }
                      />
                      <Route
                        path="/ghid-maturitate"
                        element={
                          <MainLayout>
                            <GhidMaturitate />
                          </MainLayout>
                        }
                      />
                      <Route path="/contact" element={<Contact />} />
                      <Route
                        path="/termeni-si-conditii"
                        element={<TermeniSiConditii />}
                      />
                      <Route
                        path="/politica-de-confidentialitate"
                        element={<PoliticaDeConfidentialitate />}
                      />
                      <Route
                        path="/politica-de-rambursare"
                        element={<PoliticaDeRambursare />}
                      />
                      <Route
                        path="/politica-de-cookie-uri"
                        element={<PoliticaDeCookieuri />}
                      />
                      <Route path="/legal" element={<LegalRedirect />} />

                      {/* Main application routes with sidebar */}
                      <Route
                        path="/onboarding/formular-notificare"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <FormularNotificare />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/onboarding"
                        element={
                          <Navigate
                            to="/onboarding/formular-notificare"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <StatusEntitateDashboard />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/companiile-mele"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <CompaniesPortfolio />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/onboarding/decizie-inregistrare"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <DecizieInregistrare />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/onboarding/analiza-risc"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <AnalizaRisc />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/onboarding/efectul-perturbator"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <ImpactAnalysis />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/plan-remediere"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <PlanRemediere />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Analiza de Risc */}
                      <Route
                        path="/analiza-risc"
                        element={
                          <ProtectedRoute>
                            <AnalyzaRiscResolver />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/analiza-risc/:sessionId"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <RiskWizardPage />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/identificare-raportare"
                        element={<NIS2Wizard />}
                      />

                      <Route
                        path="/evaluare-maturitate"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <EvaluareMaturitate />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/profil"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Profile />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/payment-processing"
                        element={
                          <ProtectedRoute>
                            <PaymentProcessing />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/analiza-efect-perturbator"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <AnalyzaEfectPerturbator />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/profil"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Profile />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Alternative landing pages */}
                      <Route
                        path="/landing-alternative"
                        element={<LandingAlternative />}
                      />
                      <Route
                        path="/landing-alternative-v3"
                        element={<LandingAlternativeV3 />}
                      />
                      <Route
                        path="/landing-alternative-v31"
                        element={<LandingAlternativeV31 />}
                      />
                      <Route
                        path="/landing-alternative-v40"
                        element={<LandingAlternativeV40 />}
                      />
                      <Route path="/preturi" element={<Pricing />} />
                      <Route
                        path="/cyfun-framework"
                        element={<CyFunFundamentals />}
                      />

                      {/* FAQ routes */}
                      <Route path="/nis2-faq" element={<NIS2FAQ />} />
                      <Route
                        path="/nis2-faq/browse"
                        element={<NIS2FAQBrowse />}
                      />
                      <Route
                        path="/nis2-faq/answer/:id"
                        element={<NIS2FAQAnswer />}
                      />

                      {/* Debug pages - only visible on local and staging */}
                      {(envConfig.IS_DEV || envConfig.IS_STAGING) && (
                        <>
                          <Route
                            path="/debug/file-extraction"
                            element={<FileExtractionTest />}
                          />
                          <Route
                            path="/debug/company-preview"
                            element={<DebugCompanyPreview />}
                          />
                        </>
                      )}

                      {/* Legacy wizard route redirect */}
                      <Route
                        path="/wizard"
                        element={
                          <Navigate to="/identificare-raportare" replace />
                        }
                      />

                      {/* Catch-all route */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    <CookieConsent />
                  </ImpactAnalysisModalProvider>
                </RiskAnalysisModalProvider>
              </OnboardingModalProvider>
            </BrowserRouter>
          </TooltipProvider>
        </SessionSwitcherProvider>
      </MarketingAnalyticsProvider>
    </AuthProvider>
    {/* <ReactQueryDevtools initialIsOpen={false} /> */}
  </QueryClientProvider>
);

export default App;
